import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, X, Edit2, Trash2, Trophy, IndianRupee } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SortHeader } from '../../components/SortHeader';
import { useSortableData } from '../../hooks/useSortableData';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ['Enquiry', 'Follow-up', 'Converted', 'Rejected'];
const CARD_TYPES = ['Silver', 'Gold'];

const STATUS_COLORS = {
  'Enquiry': 'bg-blue-100 text-blue-700',
  'Follow-up': 'bg-yellow-100 text-yellow-700',
  'Converted': 'bg-green-100 text-green-700',
  'Rejected': 'bg-red-100 text-red-700',
};

const emptyForm = (userName) => ({
  date: new Date().toISOString().split('T')[0],
  patient_name: '',
  patient_phone: '',
  card_type: 'Silver',
  status: 'Enquiry',
  promoted_by: userName || '',
  notes: '',
});

const HealthCardModule = ({ user }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [incentives, setIncentives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm(user.name));
  const [editId, setEditId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [enqRes, incRes] = await Promise.all([
        axios.get(`${API}/staff/healthcard`, { withCredentials: true }),
        axios.get(`${API}/staff/healthcard/incentives`, { withCredentials: true }),
      ]);
      setEnquiries(enqRes.data);
      setIncentives(incRes.data);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`${API}/staff/healthcard/${editId}`, form, { withCredentials: true });
        toast.success('Enquiry updated');
      } else {
        await axios.post(`${API}/staff/healthcard`, form, { withCredentials: true });
        toast.success('Enquiry added');
      }
      setForm(emptyForm(user.name));
      setEditId(null);
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (item) => {
    setForm({
      date: item.date || '',
      patient_name: item.patient_name || '',
      patient_phone: item.patient_phone || '',
      card_type: item.card_type || 'Silver',
      status: item.status || 'Enquiry',
      promoted_by: item.promoted_by || '',
      notes: item.notes || '',
    });
    setEditId(item.id);
    setShowForm(true);
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await axios.put(`${API}/staff/healthcard/${item.id}`, { status: newStatus }, { withCredentials: true });
      toast.success('Status updated');
      fetchData();
    } catch { toast.error('Update failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this enquiry?')) return;
    try {
      await axios.delete(`${API}/staff/healthcard/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  const filtered = enquiries.filter(e => filterStatus === 'All' || e.status === filterStatus);
  const { sorted, sortKey, sortDir, requestSort } = useSortableData(filtered, 'date', 'desc');

  const statusCounts = {};
  STATUS_OPTIONS.forEach(s => { statusCounts[s] = enquiries.filter(e => e.status === s).length; });
  const totalConverted = enquiries.filter(e => e.status === 'Converted').length;

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-[#0A2540] to-[#1E3A5F] rounded-2xl p-5 text-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Trophy className="h-5 w-5 mr-2 text-yellow-400" /> Health Card Incentive Board
          </h3>
          <span className="text-sm text-sky-300">{totalConverted} Total Conversions = Rs.{totalConverted * 20}</span>
        </div>
        {incentives.length === 0 ? (
          <p className="text-sm text-slate-300">No conversions yet. Rs.20 per converted health card!</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {incentives.sort((a, b) => b.count - a.count).map((inc, idx) => (
              <div key={inc._id} className={`rounded-xl p-3 ${idx === 0 ? 'bg-yellow-500/20 border border-yellow-400/30' : 'bg-white/10'}`}>
                <div className="flex items-center gap-2">
                  {idx === 0 && <Trophy className="h-4 w-4 text-yellow-400" />}
                  <span className="font-medium text-sm">{inc._id}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-slate-300">{inc.count} cards</span>
                  <span className="font-bold text-green-400 flex items-center"><IndianRupee className="h-3 w-3" />{inc.incentive}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilterStatus('All')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filterStatus === 'All' ? 'bg-[#0A2540] text-white' : 'bg-white text-[#475569] shadow-sm'}`}>
          All ({enquiries.length})
        </button>
        {STATUS_OPTIONS.map(s => (
          <button key={s} onClick={() => setFilterStatus(filterStatus === s ? 'All' : s)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filterStatus === s ? 'bg-[#0A2540] text-white' : 'bg-white text-[#475569] shadow-sm'}`} data-testid={`hc-filter-${s.toLowerCase()}`}>
            {s} ({statusCounts[s] || 0})
          </button>
        ))}
      </div>

      <div className="flex gap-2 justify-end">
        <Button onClick={fetchData} variant="outline" size="icon"><RefreshCw className="h-4 w-4" /></Button>
        <Button onClick={() => { setForm(emptyForm(user.name)); setEditId(null); setShowForm(true); }} className="bg-[#0A2540]" data-testid="add-healthcard-btn">
          <Plus className="h-4 w-4 mr-2" /> New Enquiry
        </Button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#0A2540]">{editId ? 'Edit' : 'New'} Health Card Enquiry</h2>
              <button onClick={() => setShowForm(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required data-testid="hc-date" /></div>
                <div>
                  <Label>Card Type</Label>
                  <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.card_type} onChange={e => setForm({ ...form, card_type: e.target.value })} data-testid="hc-card-type">
                    {CARD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div><Label>Patient Name</Label><Input value={form.patient_name} onChange={e => setForm({ ...form, patient_name: e.target.value })} required data-testid="hc-patient-name" /></div>
              <div><Label>Patient Phone</Label><Input value={form.patient_phone} onChange={e => setForm({ ...form, patient_phone: e.target.value })} required data-testid="hc-patient-phone" /></div>
              <div><Label>Promoted By</Label><Input value={form.promoted_by} onChange={e => setForm({ ...form, promoted_by: e.target.value })} data-testid="hc-promoted-by" /></div>
              <div>
                <Label>Status</Label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="hc-status">
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="hc-notes" /></div>
              <Button type="submit" className="w-full bg-[#0A2540]" data-testid="hc-submit">{editId ? 'Update' : 'Add'} Enquiry</Button>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F1F5F9]">
              <tr>
                <SortHeader label="Date" sortKey="date" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <SortHeader label="Patient" sortKey="patient_name" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <SortHeader label="Phone" sortKey="patient_phone" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} className="hidden sm:table-cell" />
                <SortHeader label="Card" sortKey="card_type" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} align="center" />
                <SortHeader label="Promoted By" sortKey="promoted_by" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <SortHeader label="Status" sortKey="status" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} align="center" />
                <th className="px-3 py-3 text-left font-semibold text-[#0A2540] hidden md:table-cell">Notes</th>
                <th className="px-3 py-3 text-right font-semibold text-[#0A2540]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {loading ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[#94A3B8]">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[#94A3B8]">No enquiries found</td></tr>
              ) : sorted.map(item => (
                <tr key={item.id} className="hover:bg-[#F8FAFC]" data-testid={`hc-row-${item.id}`}>
                  <td className="px-3 py-3 text-[#0A2540]">{item.date}</td>
                  <td className="px-3 py-3 font-medium text-[#0A2540]">{item.patient_name}</td>
                  <td className="px-3 py-3 hidden sm:table-cell"><a href={`tel:${item.patient_phone}`} className="text-[#0EA5E9] hover:underline">{item.patient_phone}</a></td>
                  <td className="px-3 py-3 text-center">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${item.card_type === 'Gold' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{item.card_type}</span>
                  </td>
                  <td className="px-3 py-3 text-[#475569]">{item.promoted_by}</td>
                  <td className="px-3 py-3 text-center">
                    <select
                      className={`text-xs font-medium px-2 py-1 rounded-full border-0 ${STATUS_COLORS[item.status] || ''}`}
                      value={item.status}
                      onChange={e => handleStatusChange(item, e.target.value)}
                      data-testid={`hc-status-${item.id}`}
                    >
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-3 hidden md:table-cell max-w-[150px] truncate text-[#475569]">{item.notes}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEdit(item)} className="p-1.5 rounded-lg hover:bg-[#F1F5F9]" data-testid={`edit-hc-${item.id}`}>
                        <Edit2 className="h-4 w-4 text-[#475569]" />
                      </button>
                      {user.role === 'admin' && (
                        <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`delete-hc-${item.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HealthCardModule;
